<script setup lang="ts">
import { computed, useId, useSlots } from 'vue';

const props = withDefaults(
    defineProps<{
        modelValue: string;
        label: string;
        type?: string;
        autocomplete?: string;
        required?: boolean;
        disabled?: boolean;
        maxlength?: number;
        minlength?: number;
        inputmode?: 'email' | 'numeric' | 'search' | 'tel' | 'text' | 'url';
        error?: string;
    }>(),
    {
        type: 'text',
        autocomplete: undefined,
        required: false,
        disabled: false,
        maxlength: undefined,
        minlength: undefined,
        inputmode: undefined,
        error: undefined,
    },
);

const emit = defineEmits<{ 'update:modelValue': [value: string] }>();
const slots = useSlots();
const errorId = useId();
const filled = computed(() => props.modelValue.length > 0);
</script>

<template>
    <div class="floating-field-wrap">
        <label
            class="floating-field"
            :class="{
                'floating-field--filled': filled,
                'floating-field--trailing': slots.trailing,
                'floating-field--disabled': disabled,
                'floating-field--invalid': error,
            }"
        >
            <input
                :value="modelValue"
                :type="type"
                :autocomplete="autocomplete"
                :required="required"
                :disabled="disabled"
                :maxlength="maxlength"
                :minlength="minlength"
                :inputmode="inputmode"
                :aria-invalid="Boolean(error)"
                :aria-describedby="error ? errorId : undefined"
                placeholder=" "
                @input="
                    emit(
                        'update:modelValue',
                        ($event.target as HTMLInputElement).value,
                    )
                "
            />
            <span
                >{{ label
                }}<em
                    v-if="required"
                    class="floating-field__required"
                    aria-hidden="true"
                    >*</em
                ></span
            >
            <div v-if="slots.trailing" class="floating-field__trailing">
                <slot name="trailing" />
            </div>
        </label>
        <p v-if="error" :id="errorId" class="floating-field__error">
            {{ error }}
        </p>
    </div>
</template>

<style scoped>
.floating-field {
    position: relative;
    display: block;
    height: 58px;
    border: 1px solid color-mix(in srgb, var(--hint) 24%, transparent);
    border-radius: 16px;
    background: var(--surface);
    transition:
        border-color 0.18s ease,
        box-shadow 0.18s ease;
}
.floating-field:focus-within {
    border-color: var(--accent);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent) 12%, transparent);
}
.floating-field--invalid,
.floating-field--invalid:focus-within {
    border-color: color-mix(in srgb, var(--danger) 72%, transparent);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--danger) 10%, transparent);
}
.floating-field--disabled {
    background: var(--secondary);
    opacity: 0.82;
}
.floating-field--disabled input {
    cursor: not-allowed;
}
.floating-field input {
    width: 100%;
    height: 100%;
    padding: 21px 16px 7px;
    border: 0;
    outline: 0;
    background: transparent;
    color: var(--text);
    font: 500 15px/1.2 var(--app-font);
}
.floating-field--trailing input {
    padding-right: 52px;
}
.floating-field > span {
    position: absolute;
    top: 50%;
    left: 16px;
    max-width: calc(100% - 32px);
    transform: translateY(-50%);
    transform-origin: left center;
    overflow: hidden;
    color: var(--hint);
    font: 500 14px/1 var(--app-font);
    white-space: nowrap;
    text-overflow: ellipsis;
    pointer-events: none;
    transition:
        top 0.18s ease,
        transform 0.18s ease,
        font-size 0.18s ease,
        color 0.18s ease;
}
.floating-field:focus-within > span,
.floating-field--filled > span {
    top: 10px;
    transform: none;
    color: var(--accent);
    font-size: 10px;
}
.floating-field--invalid > span,
.floating-field--invalid:focus-within > span,
.floating-field--invalid.floating-field--filled > span {
    color: var(--danger);
}
.floating-field__trailing {
    position: absolute;
    top: 50%;
    right: 9px;
    transform: translateY(-50%);
}
.floating-field__trailing :deep(button) {
    display: grid;
    place-items: center;
    width: 38px;
    height: 38px;
    padding: 0;
    border: 0;
    border-radius: 11px;
    background: transparent;
    color: var(--hint);
}
.floating-field__required {
    /* Part of the floating label, not a sibling, so it rides into the input with the text
       and is visible in the state where the label IS the placeholder. `aria-hidden` because
       the input already carries `required` and a screen reader announces that itself. */
    margin-left: 3px;
    color: var(--danger);
    font-style: normal;
}
.floating-field__error {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 6px 4px 0;
    color: var(--danger);
    font-size: 11px;
    line-height: 1.35;
}
.floating-field__error::before {
    content: '';
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: currentColor;
}
</style>
